import { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { reduce, map, find, keys, omitBy } from "lodash"
import { Paper } from "@mui/material"
import Table from "components/material/table"
import { UUID, ROLE, DATASET_TEAM } from "helpers/shapes"
import { ALL_DATASET_ACCESS_TEAM } from "helpers/datasets"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"

export default function DatasetTeams({ account, role, datasetId, teams, datasetTeams, update, tabs }) {
  const hasWriteAccess = role === "admin"

  const optionLabels = useMemo(() => reduce(teams, (teamsById, team) => ({
    ...teamsById,
    [team.id]: team.name,
  }), {}),
  [teams])

  // Only direct entitlements are important here
  const availableOptionLabels = useMemo(() =>
    omitBy(optionLabels, (_, id) => find(datasetTeams, member => member.id === id)),
  [datasetTeams, optionLabels])

  const addForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($datasetId: String!, $teamIds: [IdOrAlias]){
          grantDatasetAccess(id: $datasetId, teamIds: $teamIds) { id }
      }`

      const variables = {
        datasetId,
        teamIds: data.teams,
      }

      update(query, variables, account, {
        reloadData: ["datasetEntitlements", "memberDatasetEntitlements", "teamDatasetEntitlements", "userDatasets", "models", "datasetTeamEntitlements"],
      }, cb)
    }

    return {
      title: "Grant Dataset Access",
      submitText: "Grant access",
      track: {
        type: "dataset-entitlements-grant-dataset-teams",
        payload: ({ teams }) => ({
          teams,
          datasetId,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "teams",
        label: "Teams",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, availableOptionLabels, datasetId, update, account])

  const deleteForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($datasetId: String!, $teamIds: [IdOrAlias]){
        revokeDatasetAccess(id: $datasetId, teamIds: $teamIds) { id }
    }`
      const variables = {
        datasetId,
        teamIds: map(data, "id"),
      }

      update(query, variables, account, {
        reloadData: ["datasetEntitlements", "memberDatasetEntitlements", "teamDatasetEntitlements", "userDatasets", "models", "datasetTeamEntitlements"],
      }, cb)
    }

    return {
      type: "dataset-entitlement",
      track: {
        type: "dataset-entitlements-revoke-dataset-teams",
        payload: (team) => ({
          team: team.id,
          datasetId,
        }),
      },
      isEnabled: (team) => hasWriteAccess && team.alias !== ALL_DATASET_ACCESS_TEAM.ALIAS,
      renderItem: dataset => <li key={dataset.id}>{dataset.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, datasetId, update])

  const linkTo = useCallback((team) => {
    if (role !== "admin") { return null }
    const tab = team.alias !== ALL_DATASET_ACCESS_TEAM.ALIAS ? "entitlements" : "members"

    return `/${account}/home/teams/${team.alias}/${tab}`
  }, [account, role])

  return <div className="modules_dataset-entitlements-teams">
    {role === "admin" && <UpsellTabs id="dataset-teams" tabs={tabs} />}
    <Paper className="content" square>
      <Table
        title="Team entitlements"
        idField="id"
        addForm={addForm}
        editForm={null}
        deleteForm={deleteForm}
        data={datasetTeams}
        columns={[{
          name: "Name",
          id: "name",
          linkTo,
        }]}
      />
    </Paper>
  </div>
}

DatasetTeams.propTypes = {
  account: PropTypes.string.isRequired,
  datasetTeams: PropTypes.arrayOf(DATASET_TEAM).isRequired,
  datasetId: PropTypes.string.isRequired,
  userId: UUID,
  role: ROLE.isRequired,
  teams: PropTypes.arrayOf(DATASET_TEAM),
  update: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
